import { createPropertySelectors, Selector } from '@ngxs/store';
import { LoadableStatus } from '@aiwyn/shared';
import { Comment } from '../../client-requests/components/comments/comments.model';
import { CRL_STATUS_LABELS } from '../services/client-request-service-api.bridge';
import {
  CrlActionRequiredSummary,
  CrlActivityEntry,
  CrlAssignee,
  CrlClientContact,
  CrlCompletionState,
  CRL_DONE_STATUSES,
  CrlDisplaySection,
  CrlItemStatus,
  CrlItemType,
  CrlProgressSummary,
  CrlRequestAssignee,
  CrlRequestDetail,
  CrlRequestItem,
  CrlRequestReviewer,
  CrlRequestSection,
  CrlRequestServiceLine,
  CrlTeamMember,
  CrlTileCounts,
} from '../models';
import { CxpCrlDetailState } from './cxp-crl-detail.state';
import { CrlItemOptimisticPatch, CxpCrlDetailStateModel } from './cxp-crl-detail.model';

/**
 * Blended view of the selected request for the new CRL detail page.
 *
 * Composed at selector time from this state's `selectedRequestId` + `detailData`
 * (the `/page`-populated request header + sections, with assignee names/emails
 * already resolved by the backend). The request header, team, client contacts,
 * and eyebrow all read directly off `detailData`; the sections drive the
 * progress/tiles/section projections. All derivation lives here per cxp-state
 * "business rules in selectors"; components read the pre-computed values.
 */
export interface CrlSelectedRequestDisplay {
  requestId: number | null;
  /** Display name — from the `/page` detail (null until it loads). */
  name: string | null;
  /** Normalized status + label for the header pill. */
  status: CrlItemStatus | null;
  statusLabel: string | null;
  /** Request description (`/page` detail). Empty string when none. */
  description: string;
  /** Associated client entity name (`/page` detail). */
  clientName: string | null;
  /**
   * Title eyebrow: the engagement/category context shown above the request name
   * (PP-3642), e.g. `Audit · Acme LLC`. The `/page` service-line name stands in
   * for the engagement type; falls back to the client name alone when no service
   * line resolves. TODO: swap to the real engagement type when the CRL endpoints
   * carry it.
   */
  eyebrow: string | null;
  /**
   * Request-level due date (`/page` `nextDueDate`) for the right-rail "Due on"
   * line. Null when the request has no upcoming due date.
   */
  dueDate: string | null;
  /** Raw sections (ordinal order) once detail has loaded. */
  sections: CrlRequestSection[];
  /** Sections projected for display: open/finished split + completion flag. */
  displaySections: CrlDisplaySection[];
  /** Overall completion summary for the progress tile. */
  progress: CrlProgressSummary;
  /** The three granular metric-tile counts. */
  tiles: CrlTileCounts;
  /** Action-required banner summary (overdue / needs-update). */
  actionRequired: CrlActionRequiredSummary;
  /** Firm team for the right rail (owner starred first). */
  team: CrlTeamMember[];
  /** Client-access contacts for the right rail (owner starred first). */
  clientContacts: CrlClientContact[];
  /** True once `detailData` is present. */
  hasDetail: boolean;
  isLoading: boolean;
  isLoaded: boolean;
  hasError: boolean;
}

const EMPTY_PROGRESS: CrlProgressSummary = {
  done: 0,
  total: 0,
  percent: 0,
  state: CrlCompletionState.NOT_STARTED,
};

function isDone(item: CrlRequestItem): boolean {
  return CRL_DONE_STATUSES.has(item.status);
}

/** All non-N/A items flattened — the denominator for tiles/progress. */
function allItems(sections: CrlRequestSection[]): CrlRequestItem[] {
  return sections.flatMap((section) => section.items);
}

function buildProgress(items: CrlRequestItem[]): CrlProgressSummary {
  const total = items.length;
  if (total === 0) return EMPTY_PROGRESS;

  const done = items.filter(isDone).length;
  const percent = Math.round((done / total) * 100);
  const state =
    done === 0 ? CrlCompletionState.NOT_STARTED : done === total ? CrlCompletionState.COMPLETE : CrlCompletionState.PARTIALLY_COMPLETE;

  return { done, total, percent, state };
}

function buildTiles(items: CrlRequestItem[]): CrlTileCounts {
  const overdue = items.filter((item) => item.status === CrlItemStatus.OVERDUE).length;
  return {
    // Overdue items are still "waiting on you" — fold them into that tile and
    // surface the overdue subset as the red callout.
    waitingForYou: items.filter((item) => item.status === CrlItemStatus.WAITING || item.status === CrlItemStatus.OVERDUE).length,
    inReview: items.filter((item) => item.status === CrlItemStatus.IN_REVIEW).length,
    needsUpdate: items.filter((item) => item.status === CrlItemStatus.NEEDS_UPDATE).length,
    overdue,
  };
}

function buildActionRequired(items: CrlRequestItem[]): CrlActionRequiredSummary {
  const overdue = items.filter((item) => item.status === CrlItemStatus.OVERDUE).length;
  const needsUpdate = items.filter((item) => item.status === CrlItemStatus.NEEDS_UPDATE).length;
  const count = overdue + needsUpdate;
  return {
    count,
    hasOverdue: overdue > 0,
    hasNeedsUpdate: needsUpdate > 0,
    show: count > 0,
  };
}

/** 0 → 'A', 1 → 'B', … 25 → 'Z', then 'AA' … */
function sectionIdentifier(index: number): string {
  let n = index;
  let label = '';
  do {
    label = String.fromCharCode(65 + (n % 26)) + label;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return label;
}

/**
 * Projects raw sections into the display shape: items split into open vs
 * finished, a per-section completion flag, and a stable letter identifier.
 * Completed sections are sorted to the end (REQ-4) while preserving the
 * firm-defined ordinal order within each group.
 */
function buildDisplaySections(sections: CrlRequestSection[]): CrlDisplaySection[] {
  // Drop sections with no items: empty section bars never render, and the
  // section filter can't scope to them (REQ-12 — "sections with no matches are
  // hidden"). Filtered before indexing so the letter identifiers stay contiguous.
  const ordered = [...sections].filter((section) => section.items.length > 0).sort((a, b) => a.ordinal - b.ordinal);

  const projected: CrlDisplaySection[] = ordered.map((section, index) => {
    const items = [...section.items].sort((a, b) => a.ordinal - b.ordinal);
    const finishedItems = items.filter(isDone);
    const openItems = items.filter((item) => !isDone(item));
    return {
      id: section.id,
      name: section.name,
      ordinal: section.ordinal,
      identifier: sectionIdentifier(index),
      openItems,
      finishedItems,
      isComplete: items.length > 0 && openItems.length === 0,
    };
  });

  // Stable partition: incomplete sections first (ordinal order preserved),
  // then complete ones below the "completed sections" divider.
  return [...projected.filter((section) => !section.isComplete), ...projected.filter((section) => section.isComplete)];
}

/**
 * Firm team for the right rail, sourced from the `/page` reviewers (owner
 * starred first). Reviewer email is resolved by the backend on the `/page` read;
 * it falls back to an empty string when absent.
 */
function buildTeam(reviewers: CrlRequestReviewer[]): CrlTeamMember[] {
  return [...reviewers]
    .sort((a, b) => Number(b.isOwner) - Number(a.isOwner))
    .map((reviewer) => ({ staffId: reviewer.staffId, name: reviewer.name, email: reviewer.email ?? '', isOwner: reviewer.isOwner }));
}

/**
 * Client-access people for the right rail, sourced from the `/page`
 * request-level assignees — the combined union of request- and item-level
 * contact assignees plus secure-link guests. `isOwner` comes from the backend
 * (a contact assigned at the request level); it gates who may remove whom, so
 * it must never be inferred from list position. `key` gives templates a stable
 * track handle across the two identity spaces.
 */
/** The item's own access list, in the same row shape the request-level list uses. */
function buildItemClientContacts(assignees: CrlAssignee[]): CrlClientContact[] {
  return assignees.map((assignee, index) => ({
    kind: assignee.kind,
    key: `${assignee.kind}:${assignee.contactId ?? assignee.secureLinkId ?? index}`,
    contactId: assignee.contactId,
    name: assignee.name || null,
    email: assignee.email,
    isOwner: assignee.isOwner,
    secureLinkId: assignee.secureLinkId,
  }));
}

function buildClientContacts(assignees: CrlRequestAssignee[]): CrlClientContact[] {
  return assignees.map((assignee, index) => ({
    kind: assignee.kind,
    key: `${assignee.kind}:${assignee.contactId ?? assignee.secureLinkId ?? index}`,
    contactId: assignee.contactId,
    name: assignee.fullName || null,
    email: assignee.email,
    isOwner: assignee.isOwner,
    secureLinkId: assignee.secureLinkId,
  }));
}

/**
 * Builds the title eyebrow (PP-3642): `<engagement> · <client>`. The engagement
 * type is not yet on the data model, so the request's service line stands in for
 * it. When no service line resolves, the eyebrow falls back to the client name
 * alone; when neither is present it is null and the header omits the eyebrow.
 */
function buildEyebrow(serviceLine: CrlRequestServiceLine | null, clientName: string | null): string | null {
  const serviceLineName = serviceLine?.name ?? null;
  if (serviceLineName && clientName) return `${serviceLineName} · ${clientName}`;
  if (serviceLineName) return serviceLineName;
  return clientName;
}

/** Applies an optimistic patch over a single item, recomputing nothing else. */
function applyItemPatch(item: CrlRequestItem, patch: CrlItemOptimisticPatch | undefined): CrlRequestItem {
  if (!patch) return item;
  return {
    ...item,
    status: patch.status ?? item.status,
    statusLabel: patch.statusLabel ?? item.statusLabel,
    isNotApplicable: patch.isNotApplicable ?? item.isNotApplicable,
    notApplicableReason: patch.notApplicableReason !== undefined ? patch.notApplicableReason : item.notApplicableReason,
    isAwaitingReview: patch.isAwaitingReview ?? item.isAwaitingReview,
  };
}

/**
 * Merges the optimistic item patches into the loaded detail so every downstream
 * projection (tiles, sections, the panel) sees the just-applied "I'm Finished" /
 * "Mark N/A" state before the server reload lands. Returns the same reference
 * when there are no patches (cheap, stable memoization).
 */
function mergeOptimisticPatches(
  detail: CrlRequestDetail | null,
  patches: Record<number, CrlItemOptimisticPatch>,
): CrlRequestDetail | null {
  if (!detail || Object.keys(patches).length === 0) return detail;
  return {
    ...detail,
    sections: detail.sections.map((section) => ({
      ...section,
      items: section.items.map((item) => applyItemPatch(item, patches[item.id])),
    })),
  };
}

/**
 * The single item shown in the side panel (PP-3753), blended for the panel from
 * the patched detail + the request-level header context. All panel business
 * rules (which per-item actions are available, the questionnaire vs document
 * branch, section/item position) live here so the panel components stay dumb.
 */
export interface CrlSelectedItemDisplay {
  requestId: number | null;
  itemId: number | null;
  /** The resolved item (patched), or null when nothing is selected/loaded. */
  item: CrlRequestItem | null;
  /** Owning section name + letter identifier, for the overlay title + eyebrow. */
  sectionName: string | null;
  sectionIdentifier: string | null;
  /** `A.1`-style display number. */
  displayNumber: string | null;
  /** Request name (overlay breadcrumb). */
  requestName: string | null;
  /**
   * Adjacent items in display order (open-then-finished within each section,
   * completed sections last — the SAME order the list shows). The overlay's
   * back/forward arrows re-point the named outlet at these; null at the ends
   * disables the corresponding arrow. Pre-computed here so the overlay nav stays
   * dumb (cxp-state: navigation/position rules live in selectors).
   */
  prevItem: CrlRequestItem | null;
  nextItem: CrlRequestItem | null;
  /** Item description (View More source on the Documents tab). Empty when none. */
  description: string;
  /** True when the item is access-restricted (right-rail Privacy line). */
  isRestricted: boolean;
  /** True for questionnaire items (drives the answer input vs dropzone). */
  isQuestionnaire: boolean;
  isDocumentRequest: boolean;
  /** Pre-computed per-item action availability (cxp-state: rules in selectors). */
  canMarkFinished: boolean;
  /** True when the item reads as finished, whether or not Undo is still offered. */
  isMarkedFinished: boolean;
  canUndoFinished: boolean;
  canMarkNotApplicable: boolean;
  /**
   * True when the item has no response to submit: a document-request item with no
   * client-uploaded file, or a questionnaire with no saved answer. "I'm Finished"
   * stays visible but inert. "Mark as Not Applicable" is untouched — it is the
   * other valid way to answer, and an item must leave its open status by one
   * route or the other.
   *
   * Firm-provided reference files are not a response; the client has to have
   * added something themselves. Files and answer both load lazily, so this reads
   * true for the moment before `/item/detail` lands, keeping the client from
   * confirming against a list that is not yet showing what it holds.
   */
  isMissingResponse: boolean;
  /** True when the item is already N/A and this caller may return it to its open state. */
  canUndoNotApplicable: boolean;
  /**
   * True when the client may still upload files for this item — a non-questionnaire
   * item whose status is editable (open / overdue / changes-requested). Drives the
   * dropzone's visibility + the upload guard; false in review + done states, so the
   * only way back to uploading is Undo "I'm Finished".
   */
  canUploadFiles: boolean;
  /** True while this item's undo-finished call is in flight (Undo disabled). */
  isUndoPending: boolean;
  /**
   * Restricted-visibility audit view: the item is masked for this caller (a
   * client owner NOT assigned to a restricted item). They may see it exists +
   * its status, but must not interact with it or see its private content
   * (comments / activity). The inverse of `canInteract`; downstream surfaces
   * hide the invite link, the comment composer, and the comments/activity
   * content (which is NOT fetched) behind a restricted placeholder.
   */
  isAuditView: boolean;
  /** True once the item could be resolved from loaded detail (vs not-yet-loaded). */
  isResolved: boolean;
  /**
   * Who has access to THIS item: its assigned contacts plus the guests granted to
   * it. Distinct from `CrlSelectedRequestDisplay.clientContacts`, which unions
   * every item's people — access is granted and revoked per item.
   */
  clientContacts: CrlClientContact[];
}

const EMPTY_ITEM_DISPLAY: CrlSelectedItemDisplay = {
  requestId: null,
  itemId: null,
  item: null,
  sectionName: null,
  sectionIdentifier: null,
  displayNumber: null,
  requestName: null,
  prevItem: null,
  nextItem: null,
  description: '',
  isRestricted: false,
  isQuestionnaire: false,
  isDocumentRequest: false,
  canMarkFinished: false,
  isMarkedFinished: false,
  canUndoFinished: false,
  canMarkNotApplicable: false,
  isMissingResponse: false,
  canUndoNotApplicable: false,
  canUploadFiles: false,
  isUndoPending: false,
  isAuditView: false,
  isResolved: false,
  clientContacts: [],
};

/**
 * The request's own item order — sections and items by ordinal.
 *
 * Deliberately not the rendered order, which sorts finished items to the end of
 * their section: navigating by that gives a finished item the neighbours it sorts
 * beside rather than the ones either side of it in the request. Open items are
 * unaffected either way, since they already render in ordinal order.
 */
function flattenNavigationItems(displaySections: CrlDisplaySection[]): CrlRequestItem[] {
  return [...displaySections]
    .sort((sectionA, sectionB) => sectionA.ordinal - sectionB.ordinal)
    .flatMap((section) => [...section.openItems, ...section.finishedItems].sort((itemA, itemB) => itemA.ordinal - itemB.ordinal));
}

/**
 * Scans outward from `position` rather than walking a pre-filtered list, so a
 * finished item — reachable from the list and by URL — still offers its
 * neighbours instead of dead-ending.
 */
function findNearestOpenItem(items: CrlRequestItem[], position: number, step: 1 | -1): CrlRequestItem | null {
  if (position < 0) {
    return null;
  }

  for (let index = position + step; index >= 0 && index < items.length; index += step) {
    if (!isDone(items[index])) {
      return items[index];
    }
  }

  return null;
}

/**
 * Combined activity-feed view for the overlay's Activity tab + rail timeline.
 * `entries` are the selected item's cached feed (empty until its first load);
 * `isLoading` is true while THIS item's fetch is in flight.
 */
export interface CrlItemActivityView {
  entries: CrlActivityEntry[];
  isLoading: boolean;
}

const EMPTY_ACTIVITY_VIEW: CrlItemActivityView = { entries: [], isLoading: false };

/**
 * Combined comments view for the overlay's Comments tab. `comments` are the
 * selected item's cached thread (empty until its first load); `isLoading` is true
 * while THIS item's fetch is in flight.
 */
export interface CrlItemCommentsView {
  comments: Comment[];
  isLoading: boolean;
}

const EMPTY_COMMENTS_VIEW: CrlItemCommentsView = { comments: [], isLoading: false };

/** One selectable item in the Invite dialog's section/item tree. */
export interface CrlInviteTreeItem {
  id: number;
  name: string;
  /** `A.1`-style display number, matching the list and the overlay header. */
  displayNumber: string;
  /**
   * Whether access may still be granted on this item. Access is per item and
   * frozen once the item leaves the client's hands, so an item that cannot take
   * a new recipient is offered as a disabled row rather than hidden — the tree
   * stays a faithful picture of the request.
   */
  canInvite: boolean;
  /**
   * Emails of everyone who already has access, normalized (trimmed +
   * lowercased) so the dialog's live email match is a plain comparison. People
   * without an email on record are omitted.
   */
  assigneeEmails: string[];
}

/** One section in the Invite dialog's tree, in the list's display order. */
export interface CrlInviteTreeSection {
  id: number;
  name: string;
  /** Section letter (`A`, `B`, …), the same one the list renders. */
  identifier: string;
  items: CrlInviteTreeItem[];
}

/**
 * Whether a new person can be given access to this item: an item masked for this
 * caller or restricted to other recipients is not theirs to share, and a
 * finished or submitted item has a frozen access list.
 *
 * These are the per-item gates only. Whether the caller may manage access at all
 * is a separate question the rail answers before it offers Invite, and it cannot
 * be answered from state — a secure-link guest is identified by the session, not
 * by anything on the item.
 */
function canInviteToItem(item: CrlRequestItem): boolean {
  return (
    !item.isAnswerMasked && !item.isRestricted && !CRL_DONE_STATUSES.has(item.status) && item.status !== CrlItemStatus.IN_REVIEW
  );
}

function buildInviteTreeItem(item: CrlRequestItem, sectionIdentifier: string): CrlInviteTreeItem {
  return {
    id: item.id,
    name: item.name,
    displayNumber: `${sectionIdentifier}.${item.ordinal}`,
    canInvite: canInviteToItem(item),
    assigneeEmails: (item.assignees ?? []).map((assignee) => assignee.email?.trim().toLowerCase() ?? '').filter((email) => email !== ''),
  };
}

export class CxpCrlDetailSelectors {
  static readonly slices = createPropertySelectors<CxpCrlDetailStateModel>(CxpCrlDetailState);

  @Selector([CxpCrlDetailSelectors.slices.selectedRequestId])
  static getSelectedRequestId(selectedRequestId: number | null): number | null {
    return selectedRequestId;
  }

  @Selector([CxpCrlDetailSelectors.slices.detailData])
  static getDetailData(detailData: CrlRequestDetail | null): CrlRequestDetail | null {
    return detailData;
  }

  @Selector([CxpCrlDetailSelectors.slices.selectedItemId])
  static getSelectedItemId(selectedItemId: number | null): number | null {
    return selectedItemId;
  }

  /**
   * Activity feed for the currently-selected item, read from the per-item cache.
   * A previously-loaded feed renders immediately on return to an item, while
   * `isLoading` reflects an in-flight (re)fetch for that same item.
   */
  @Selector([
    CxpCrlDetailSelectors.slices.selectedItemId,
    CxpCrlDetailSelectors.slices.itemActivityByItem,
    CxpCrlDetailSelectors.slices.itemActivityLoadingItemId,
  ])
  static getSelectedItemActivity(
    selectedItemId: number | null,
    itemActivityByItem: Record<number, CrlActivityEntry[]> = {},
    itemActivityLoadingItemId: number | null,
  ): CrlItemActivityView {
    if (selectedItemId === null) return EMPTY_ACTIVITY_VIEW;
    return {
      entries: itemActivityByItem[selectedItemId] ?? [],
      isLoading: itemActivityLoadingItemId === selectedItemId,
    };
  }

  /**
   * Comments thread for the currently-selected item, read from the per-item
   * cache — same stale-while-revalidate behaviour as {@link getSelectedItemActivity}.
   */
  @Selector([
    CxpCrlDetailSelectors.slices.selectedItemId,
    CxpCrlDetailSelectors.slices.itemCommentsByItem,
    CxpCrlDetailSelectors.slices.itemCommentsLoadingItemId,
  ])
  static getSelectedItemComments(
    selectedItemId: number | null,
    itemCommentsByItem: Record<number, Comment[]> = {},
    itemCommentsLoadingItemId: number | null,
  ): CrlItemCommentsView {
    if (selectedItemId === null) return EMPTY_COMMENTS_VIEW;
    return {
      comments: itemCommentsByItem[selectedItemId] ?? [],
      isLoading: itemCommentsLoadingItemId === selectedItemId,
    };
  }

  /** Loaded detail with optimistic item patches merged in (the render source). */
  @Selector([CxpCrlDetailSelectors.slices.detailData, CxpCrlDetailSelectors.slices.optimisticItemPatches])
  static getPatchedDetailData(
    detailData: CrlRequestDetail | null,
    optimisticItemPatches: Record<number, CrlItemOptimisticPatch>,
  ): CrlRequestDetail | null {
    return mergeOptimisticPatches(detailData, optimisticItemPatches);
  }

  /**
   * THE composed selector for downstream tickets. Blends this state's selection
   * + loaded detail with the raw task-list request and tasks support data.
   * Merge + all list-view business rules live here, not in the bridge or any
   * component.
   */
  @Selector([
    CxpCrlDetailSelectors.slices.selectedRequestId,
    CxpCrlDetailSelectors.getPatchedDetailData,
    CxpCrlDetailSelectors.slices.detailStatus,
  ])
  static getSelectedRequestDisplay(
    selectedRequestId: number | null,
    detailData: CrlRequestDetail | null,
    detailStatus: LoadableStatus,
  ): CrlSelectedRequestDisplay {
    // Every request-header field now comes from the `/page`-populated detail, so
    // the header is null until detail arrives (the reload keeps the prior detail
    // through the loading state). Status is null until detail loads.
    const name = detailData?.name ?? null;
    const description = detailData?.description ?? '';
    const status = detailData ? detailData.status : null;
    const statusLabel = status !== null ? CRL_STATUS_LABELS[status] : null;
    const clientName = detailData?.clientName ?? null;

    // Item assignee names + right-rail client-contact names/emails arrive
    // resolved on the `/page` read, so the sections render as-is.
    const sections = detailData?.sections ?? [];
    const items = allItems(sections);

    return {
      requestId: selectedRequestId,
      name,
      status,
      statusLabel,
      description,
      clientName,
      eyebrow: buildEyebrow(detailData?.serviceLine ?? null, clientName),
      dueDate: detailData?.nextDueDate ?? null,
      sections,
      displaySections: buildDisplaySections(sections),
      progress: buildProgress(items),
      tiles: buildTiles(items),
      actionRequired: buildActionRequired(items),
      team: buildTeam(detailData?.reviewers ?? []),
      clientContacts: buildClientContacts(detailData?.assignees ?? []),
      hasDetail: detailData !== null,
      isLoading: detailStatus === LoadableStatus.LOADING,
      isLoaded: detailStatus === LoadableStatus.LOADED,
      hasError: detailStatus === LoadableStatus.ERROR,
    };
  }

  /**
   * The item currently shown in the side panel (PP-3753). Resolves the
   * `selectedItemId` against the patched detail, locates its section (reusing
   * the same letter-identifier projection the list uses so `A.1` matches), and
   * pre-computes which per-item actions are available. Returns a stable empty
   * shape when nothing is selected or detail hasn't loaded — the panel reads
   * `isResolved` to decide between content and a skeleton.
   */
  @Selector([
    CxpCrlDetailSelectors.slices.selectedRequestId,
    CxpCrlDetailSelectors.slices.selectedItemId,
    CxpCrlDetailSelectors.getPatchedDetailData,
    CxpCrlDetailSelectors.getSelectedRequestDisplay,
    CxpCrlDetailSelectors.slices.pendingUndoItemIds,
  ])
  static getSelectedItemDisplay(
    selectedRequestId: number | null,
    selectedItemId: number | null,
    detailData: CrlRequestDetail | null,
    requestDisplay: CrlSelectedRequestDisplay,
    pendingUndoItemIds: number[] = [],
  ): CrlSelectedItemDisplay {
    if (selectedItemId === null || !detailData) {
      return { ...EMPTY_ITEM_DISPLAY, requestId: selectedRequestId, itemId: selectedItemId, requestName: requestDisplay.name };
    }

    // Find the item and its owning display-section (identifiers already computed).
    const displaySection = requestDisplay.displaySections.find((section) =>
      [...section.openItems, ...section.finishedItems].some((item) => item.id === selectedItemId),
    );
    const item =
      displaySection?.openItems.find((entry) => entry.id === selectedItemId) ??
      displaySection?.finishedItems.find((entry) => entry.id === selectedItemId) ??
      null;

    if (!item || !displaySection) {
      return { ...EMPTY_ITEM_DISPLAY, requestId: selectedRequestId, itemId: selectedItemId, requestName: requestDisplay.name, isResolved: true };
    }

    const isQuestionnaire = item.type === CrlItemType.QUESTIONNAIRE;
    const isDocumentRequest = item.type === CrlItemType.DOCUMENT_REQUEST;
    const isDone = CRL_DONE_STATUSES.has(item.status);

    // Restricted-visibility audit view: a restricted item whose content is masked
    // for this caller (the client owner who is NOT an assignee on it). They may
    // see it exists + its activity, but must not act on it — no Mark Finished /
    // Mark N/A / Undo / upload. `isAnswerMasked` is the per-caller mask the
    // backend sets, and is only ever true for a restricted item the caller isn't
    // authorized to see, so it is the authoritative "audit-only" gate here.
    const canInteract = !item.isAnswerMasked;

    const linearItems = flattenNavigationItems(requestDisplay.displaySections);
    const position = linearItems.findIndex((entry) => entry.id === selectedItemId);

    return {
      requestId: selectedRequestId,
      itemId: selectedItemId,
      item,
      sectionName: displaySection.name,
      sectionIdentifier: displaySection.identifier,
      displayNumber: `${displaySection.identifier}.${item.ordinal}`,
      requestName: requestDisplay.name,
      prevItem: findNearestOpenItem(linearItems, position, -1),
      nextItem: findNearestOpenItem(linearItems, position, 1),
      description: item.description ?? '',
      isRestricted: item.isRestricted ?? false,
      isQuestionnaire,
      isDocumentRequest,
      // "I'm Finished" applies while the item is still open (not already done/in-review/N/A).
      // Every per-item action is additionally gated on `canInteract` so a masked
      // restricted item is audit-only (see above).
      canMarkFinished: canInteract && !isDone && item.status !== CrlItemStatus.IN_REVIEW,
      // Undo applies only while the item is submitted but not yet accepted by
      // the firm, and is not N/A. Keyed on the raw-status-derived
      // `isAwaitingReview` rather than the display status, which cannot tell
      // submitted from firm-accepted while both render as FINISHED.
      canUndoFinished: canInteract && !!item.isAwaitingReview && !item.isNotApplicable,
      // The item reads as finished once the client has submitted it, and keeps
      // reading that way after the firm accepts it — acceptance removes the
      // Undo, not the fact that it is finished. Named separately from
      // `canUndoFinished` for that reason. `isAwaitingReview` covers the case
      // where CRL_IN_REVIEW_ENABLED puts a submitted item outside the done
      // bucket; an N/A item states its own condition instead.
      isMarkedFinished: !item.isNotApplicable && (isDone || !!item.isAwaitingReview),
      // Marking N/A is a way of answering the item, so it is offered exactly
      // where "I'm Finished" is — on an item the client can still respond to —
      // and additionally only while the item is not already N/A. Naming
      // IN_REVIEW as well as the done bucket matters when
      // CRL_IN_REVIEW_ENABLED surfaces that state: a submitted item is then
      // outside the done bucket, and must still not be markable.
      canMarkNotApplicable: canInteract && !isDone && item.status !== CrlItemStatus.IN_REVIEW && !item.isNotApplicable,
      // Each item type answers in its own way: a questionnaire with text, a
      // document request with a file the CLIENT uploaded. The firm's reference
      // files are the question, not the answer.
      isMissingResponse: isQuestionnaire
        ? (item.answer ?? '').trim().length === 0
        : isDocumentRequest && !(item.files ?? []).some((file) => !file.isReference),
      // Undoing N/A is the way back from it, but only while the item is still
      // the client's to change: once the firm has accepted it there is nothing
      // to take back. Keyed on the raw-status `isAccepted` rather than the
      // display status, which reads as done for EVERY N/A item — and not on
      // `isAwaitingReview`, which would strand an item whose N/A annotation
      // landed while its submission call failed.
      canUndoNotApplicable: canInteract && item.isNotApplicable && !item.isAccepted,
      // Uploads are allowed only on non-questionnaire, non-N/A items whose RAW
      // backend status is open or changes-requested — locked in review + done
      // states (so the only way back to uploading is Undo "I'm Finished").
      canUploadFiles: canInteract && !isQuestionnaire && !item.isNotApplicable && !!item.isEditableStatus,
      isUndoPending: pendingUndoItemIds.includes(selectedItemId),
      clientContacts: buildItemClientContacts(item.assignees ?? []),
      // Audit-only when the item is masked for this caller (the inverse of the
      // interaction gate) — hides the invite link + comment composer and swaps
      // comments/activity content for a restricted placeholder downstream.
      isAuditView: !canInteract,
      isResolved: true,
    };
  }

  /**
   * The request's sections and items as the Invite dialog's checkbox tree, in
   * the order the list shows them (open items first within each section,
   * completed sections last). Only items this caller can see are in the loaded
   * detail at all, so the tree is scoped by the server rather than filtered
   * here; per-item eligibility and email normalization are pre-computed so the
   * dialog only has to match the address being typed.
   */
  @Selector([CxpCrlDetailSelectors.getSelectedRequestDisplay])
  static getInviteTreeSections(requestDisplay: CrlSelectedRequestDisplay): CrlInviteTreeSection[] {
    return requestDisplay.displaySections.map((section) => ({
      id: section.id,
      name: section.name,
      identifier: section.identifier,
      items: [...section.openItems, ...section.finishedItems].map((item) => buildInviteTreeItem(item, section.identifier)),
    }));
  }
}
